"use client";

import { ContextChips } from "@/components/prompt-composer/context-chips";
import { DndOverlay } from "@/components/chat-panel/dnd-overlay";
import {
  PromptInputTextarea,
  PromptInputToolbar,
} from "@/components/ai-elements/prompt-input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUploadFiles } from "@/hooks/use-upload-files";
import { cn } from "@/lib/utils";
import { imagineStore } from "@/stores/imagine.store";
import {
  AttachmentVariant,
  PromptAddonType,
} from "@/stores/slices/prompt-context.slice";
import { PaperClipIcon } from "@heroicons/react/24/solid";
import { CircleAlert } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { EnhancePromptButton } from "../ai-elements/enhance-prompt-button";

const MAX_ATTACHMENTS_PER_MESSAGE = 3;
export const SUPPORTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
export const SUPPORTED_FILE_TYPES = ["application/pdf"];
export const SUPPORTED_ATTACHMENT_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_FILE_TYPES,
];

type ComposerContext = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  previousPrompt: string | null;
  onPreviousPromptChange: (prompt: string | null) => void;
  isFocused: boolean;
  onFocusChange: (isFocused: boolean) => void;
  isDragActive: boolean;
  onDragActiveChange: (isDragActive: boolean) => void;
  uploadAttachments: (files: Array<File>) => Promise<void>;
  renderErrorToast: (message: string) => void;
  inputTextAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  inputFileRef: React.RefObject<HTMLInputElement | null>;
};

const ComposerContext = createContext<ComposerContext | null>(null);

const useComposer = () => {
  const context = useContext(ComposerContext);
  if (!context) {
    throw new Error(
      "Composer components must be used within a ComposerProvider",
    );
  }
  return context;
};

type ComposerProviderProps = {
  children: React.ReactNode;
} & Pick<
  ComposerContext,
  "prompt" | "onPromptChange" | "previousPrompt" | "onPreviousPromptChange"
>;

export const ComposerProvider = ({
  prompt,
  onPromptChange,
  previousPrompt,
  onPreviousPromptChange,
  children,
}: ComposerProviderProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const addons = imagineStore.use.promptContext.addons();
  const addAddon = imagineStore.use.promptContext.addAddon();
  const updateAddon = imagineStore.use.promptContext.updateAddon();
  const removeAddon = imagineStore.use.promptContext.removeAddon();
  const attachments = useMemo(
    () => addons.filter((addon) => addon.type === PromptAddonType.ATTACHMENT),
    [addons],
  );
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const fileToAttachmentId = useRef<Record<string, string>>({}).current;

  const renderErrorToast = (message: string) => {
    return toast.error(message, {
      icon: <CircleAlert className="size-4 text-red-500" />,
    });
  };

  const handleUploadSuccess = (data: { url: string }, file: File) => {
    const attachmentId = fileToAttachmentId[`${file.name}-${file.type}`]!;
    updateAddon(attachmentId, {
      url: data.url,
      uploadStatus: "success",
      removable: true,
    });
  };

  const handleUploadError = (_: Error, file: File) => {
    const attachmentId = fileToAttachmentId[`${file.name}-${file.type}`]!;
    removeAddon(attachmentId);
    renderErrorToast(`Failed to upload ${file.name}`);
  };

  const handleUploadMutate = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const uploadFiles = useUploadFiles({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
    onMutate: handleUploadMutate,
  });

  const uploadAttachments = async (files: Array<File>) => {
    const totalAttachments = attachments.length + files.length;
    if (totalAttachments > MAX_ATTACHMENTS_PER_MESSAGE) {
      renderErrorToast("Only provide up to 3 attachments per message");
      return;
    }

    if (files.some((file) => !SUPPORTED_ATTACHMENT_TYPES.includes(file.type))) {
      renderErrorToast(
        "Unsupported attachment types - use JPG, PNG, WEBP, or PDF only",
      );
      return;
    }

    files.forEach((file) => {
      const attachmentId = addAddon(PromptAddonType.ATTACHMENT, {
        url: "",
        variant: SUPPORTED_IMAGE_TYPES.includes(file.type)
          ? AttachmentVariant.IMAGE
          : AttachmentVariant.FILE,
        mediaType: file.type,
        filename: file.name,
        uploadStatus: "uploading",
        removable: false,
      });
      fileToAttachmentId[`${file.name}-${file.type}`] = attachmentId;
    });

    await uploadFiles(files);
  };

  const context: ComposerContext = {
    prompt,
    onPromptChange,
    previousPrompt,
    onPreviousPromptChange,
    isFocused,
    onFocusChange: setIsFocused,
    isDragActive,
    onDragActiveChange: setIsDragActive,
    uploadAttachments,
    renderErrorToast,
    inputTextAreaRef,
    inputFileRef,
  };

  return (
    <ComposerContext.Provider value={context}>
      {children}
    </ComposerContext.Provider>
  );
};

export const ComposerFrame = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { isFocused, isDragActive } = useComposer();

  return (
    <div
      className={cn(
        "h-full w-full overflow-hidden border rounded-2xl bg-card transition-all duration-200 z-9",
        isFocused && "ring-1 ring-inset ring-muted-foreground/40",
        isDragActive && "border-dashed",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const ComposerDropzone = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { uploadAttachments, renderErrorToast, onDragActiveChange } =
    useComposer();
  const addons = imagineStore.use.promptContext.addons();
  const attachments = useMemo(
    () => addons.filter((addon) => addon.type === PromptAddonType.ATTACHMENT),
    [addons],
  );

  const handleDropRejected = (fileRejections: Array<FileRejection>) => {
    const totalAttachments = attachments.length + fileRejections.length;
    if (totalAttachments > MAX_ATTACHMENTS_PER_MESSAGE) {
      renderErrorToast("Only provide up to 3 attachments per message");
      return;
    }
    renderErrorToast(
      "Unsupported attachment types - use JPG, PNG, WEBP, or PDF only",
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: uploadAttachments,
    onDropRejected: handleDropRejected,
    accept: {
      ...SUPPORTED_ATTACHMENT_TYPES.reduce(
        (acc, type) => {
          acc[type] = [`.${type.split("/")[1]}`];
          return acc;
        },
        {} as Record<string, Array<string>>,
      ),
    },
    maxFiles: MAX_ATTACHMENTS_PER_MESSAGE,
    noClick: true,
  });

  useEffect(() => {
    onDragActiveChange(isDragActive);
  }, [isDragActive]);

  return (
    <div className={cn("h-full w-full relative")} {...getRootProps()}>
      {isDragActive && <DndOverlay />}
      {children}
      <input {...getInputProps()} className="hidden" />
    </div>
  );
};

export const ComposerChips = () => {
  const addons = imagineStore.use.promptContext.addons();
  const visibleAddons = addons.filter((addon) => !addon.props.hidden);

  if (!visibleAddons.length) {
    return null;
  }

  return (
    <div className="flex items-center flex-wrap gap-2 min-w-0 px-3 pt-2 pb-1">
      <ContextChips addons={addons} />
    </div>
  );
};

export const ComposerTextarea = ({
  placeholder,
  disabled,
  className,
}: {
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) => {
  const {
    prompt,
    onPromptChange,
    onPreviousPromptChange,
    onFocusChange,
    uploadAttachments,
    inputTextAreaRef,
  } = useComposer();

  return (
    <PromptInputTextarea
      ref={inputTextAreaRef}
      autoFocus
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        "text-sm text-foreground/80 placeholder:text-muted-foreground placeholder:text-sm",
        className,
      )}
      onChange={(e) => {
        onPromptChange(e.target.value);
        onPreviousPromptChange(null);
      }}
      value={prompt}
      onPaste={(e) => {
        if (e.clipboardData.files?.length) {
          e.preventDefault();
          e.stopPropagation();
          uploadAttachments(Array.from(e.clipboardData.files));
        }
      }}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
    />
  );
};

export const ComposerToolbar = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <PromptInputToolbar className="flex justify-between items-end h-10 px-2 pb-2.5">
      {children}
    </PromptInputToolbar>
  );
};

export const ComposerEnhancePromptButton = ({
  disabled,
}: {
  disabled: boolean;
}) => {
  const {
    prompt,
    onPromptChange,
    previousPrompt,
    onPreviousPromptChange,
    inputTextAreaRef,
  } = useComposer();

  const handleError = (message: string) => {
    toast.error(message, {
      duration: 5000,
    });
  };

  return (
    <EnhancePromptButton
      id="chat-enhance-prompt"
      prompt={prompt}
      mode={previousPrompt ? "undo" : "enhance"}
      label={previousPrompt ? "Undo enhancement" : undefined}
      onUndo={() => {
        if (!previousPrompt) {
          return;
        }
        onPromptChange(previousPrompt);
        onPreviousPromptChange(null);
        inputTextAreaRef.current?.focus();
      }}
      onComplete={({ enhancedPrompt, reason }) => {
        if (enhancedPrompt) {
          onPreviousPromptChange(prompt);
          onPromptChange(enhancedPrompt);
          inputTextAreaRef.current?.focus();
        } else if (reason) {
          handleError(reason);
        }
      }}
      onError={handleError}
      disabled={disabled}
    />
  );
};

export const ComposerAttachFileButton = ({
  disabled = false,
}: {
  disabled?: boolean;
}) => {
  const { inputFileRef, inputTextAreaRef, uploadAttachments } = useComposer();

  const triggerFileInput = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
      inputTextAreaRef.current?.focus();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) {
      return;
    }

    const files: Array<File> = [];
    for (const file of e.target.files) {
      files.push(file);
    }
    uploadAttachments(files);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cursor-pointer text-muted-foreground/70 p-0 size-8"
          onClick={triggerFileInput}
          disabled={disabled}
        >
          <PaperClipIcon className="size-5" />
          <input
            ref={inputFileRef}
            type="file"
            className="hidden"
            accept={SUPPORTED_ATTACHMENT_TYPES.join(", ")}
            onChange={handleFileInputChange}
            multiple
          />
        </Button>
      </TooltipTrigger>
      <TooltipContent sideOffset={4}>Attach file</TooltipContent>
    </Tooltip>
  );
};
